import { Button, makeStyles } from '@fluentui/react-components';
import {
  ArrowDownload20Regular,
  ArrowSync20Regular,
  Delete20Regular,
  Document20Regular,
} from '@fluentui/react-icons';
import { downloadUrl, type ProjectFile } from '../api/projects.js';

const useStyles = makeStyles({
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-s)',
  },
  empty: {
    padding: 'var(--space-l)',
    color: 'var(--text-tertiary)',
    fontSize: 'var(--fs-200)',
    textAlign: 'center',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr auto',
    gap: 'var(--space-m)',
    alignItems: 'center',
    padding: 'var(--space-m) var(--space-l)',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 'var(--radius-md)',
  },
  icon: { color: 'var(--text-tertiary)' },
  meta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  name: {
    fontSize: 'var(--fs-300)',
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  sub: {
    fontFamily: 'var(--font-mono)',
    fontSize: 'var(--fs-100)',
    color: 'var(--text-tertiary)',
    display: 'flex',
    gap: 'var(--space-s)',
    alignItems: 'center',
  },
  actions: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  error: {
    fontSize: 'var(--fs-100)',
    color: 'var(--semantic-danger)',
    marginTop: '2px',
    wordBreak: 'break-word',
  },
  // Un archivo que no se pudo leer por su formato no es un error del sistema: se
  // explica en tono neutro, no en rojo de alarma.
  motivo: {
    fontSize: 'var(--fs-100)',
    color: 'var(--text-secondary)',
    marginTop: '2px',
    wordBreak: 'break-word',
  },
});

const statusStyle: Record<
  ProjectFile['parseStatus'],
  { bg: string; fg: string; label: string }
> = {
  ok:      { bg: 'rgba(16,185,129,0.15)', fg: 'var(--semantic-success)', label: 'leído' },
  parsing: { bg: 'rgba(245,158,11,0.15)', fg: 'var(--semantic-warning)', label: 'leyendo…' },
  pending: { bg: 'var(--bg-raised)',      fg: 'var(--text-tertiary)',    label: 'pendiente' },
  failed:  { bg: 'rgba(239,68,68,0.15)',  fg: 'var(--semantic-danger)',  label: 'error al leer' },
  skipped: { bg: 'var(--bg-raised)',      fg: 'var(--text-tertiary)',    label: 'no compatible' },
};

function fmtSize(bytes: number | null): string {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

interface Props {
  projectId: string;
  files: ProjectFile[];
  /** Si `undefined`, no se muestra el botón eliminar (modo solo lectura). */
  onDelete?: (file: ProjectFile) => Promise<void>;
  /** Si `undefined`, no se muestra el botón reintentar (modo solo lectura). */
  onRetry?: (file: ProjectFile) => Promise<void>;
  /** ci: sin la insignia "no compatible" (queda el motivo en texto, si lo hay). */
  sinInsigniaNoCompatible?: boolean;
  /** Texto cuando no hay archivos. */
  textoVacio?: string;
}

export function FilesPanel({ projectId, files, onDelete, onRetry, sinInsigniaNoCompatible, textoVacio }: Props) {
  const styles = useStyles();

  if (files.length === 0) {
    return (
      <div className={styles.empty}>
        {textoVacio ?? (onDelete ? 'Aún no hay archivos. Súbelos arriba.' : 'No hay archivos cargados.')}
      </div>
    );
  }

  return (
    <div className={styles.panel}>
      {files.map((f) => (
        <div key={f.id} className={styles.row}>
          <Document20Regular className={styles.icon} />
          <div className={styles.meta}>
            <div className={styles.name} title={f.originalName}>
              {f.originalName}
            </div>
            <div className={styles.sub}>
              <span>{fmtSize(f.sizeBytes)}</span>
              {!(sinInsigniaNoCompatible && f.parseStatus === 'skipped') && (
              <>
              <span>·</span>
              <span
                style={{
                  padding: '1px 8px',
                  borderRadius: 'var(--radius-circular)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  background: statusStyle[f.parseStatus].bg,
                  color: statusStyle[f.parseStatus].fg,
                }}
              >
                {statusStyle[f.parseStatus].label}
              </span>
              </>
              )}
              {f.classification && (
                <>
                  <span>·</span>
                  <span>{f.classification}</span>
                </>
              )}
            </div>
            {f.parseStatus === 'failed' && f.parseError && (
              <div className={styles.error} title={f.parseError}>
                {f.parseError}
              </div>
            )}
            {/* Un archivo salteado traía el motivo guardado y no se mostraba: el
                usuario veía "NO COMPATIBLE" sin saber por qué ni qué hacer. */}
            {f.parseStatus === 'skipped' && f.parseError && (
              <div className={styles.motivo} title={f.parseError}>
                {f.parseError}
              </div>
            )}
          </div>
          <div className={styles.actions}>
            {onRetry && f.parseStatus === 'failed' && (
              <Button
                size="small"
                appearance="subtle"
                icon={<ArrowSync20Regular />}
                aria-label="Reintentar lectura"
                title="Reintentar lectura"
                onClick={() => void onRetry(f)}
              />
            )}
            <Button
              as="a"
              href={downloadUrl(projectId, f.id)}
              size="small"
              appearance="subtle"
              icon={<ArrowDownload20Regular />}
              aria-label="Descargar"
            />
            {onDelete && (
              <Button
                size="small"
                appearance="subtle"
                icon={<Delete20Regular />}
                aria-label="Eliminar"
                onClick={() => void onDelete(f)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
